import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material';
import GpsFixedIcon from '@mui/icons-material/GpsFixed';
import LocationSearchingIcon from '@mui/icons-material/LocationSearching';
import {
  formatDistance,
  formatVolume,
  formatTime,
  formatNumericHours,
} from '../common/util/formatter';
import ReportFilter from './components/ReportFilter';
import { useAttributePreference } from '../common/util/preferences';
import { useTranslation } from '../common/components/LocalizationProvider';
import PageLayout from '../common/components/PageLayout';
import ReportsMenu from './components/ReportsMenu';
import ColumnSelect from './components/ColumnSelect';
import usePersistedState from '../common/util/usePersistedState';
import { useCatch } from '../reactHelper';
import useReportStyles from './common/useReportStyles';
import MapPositions from '../map/MapPositions';
import MapView from '../map/core/MapView';
import MapCamera from '../map/MapCamera';
import AddressValue from '../common/components/AddressValue';
import TableShimmer from '../common/components/TableShimmer';
import MapGeofence from '../map/MapGeofence';
import scheduleReport from './common/scheduleReport';
import MapScale from '../map/MapScale';
import fetchOrThrow from '../common/util/fetchOrThrow';
import { nativePostMessage } from '../common/components/NativeInterface';
import { sessionActions } from '../store';
import PrintHeader from './common/PrintHeader';
import StatusCard from '../common/components/StatusCard';
import './common/printStyles.css';
import { useTheme } from '@mui/material/styles';
import exportExcel from '../common/util/exportExcel';

const columnsArray = [
  ['startTime', 'reportStartTime'],
  ['startOdometer', 'positionOdometer'],
  ['address', 'positionAddress'],
  ['endTime', 'reportEndTime'],
  ['duration', 'reportDuration'],
  ['engineHours', 'reportEngineHours'],
  ['spentFuel', 'reportSpentFuel'],
];
const columnsMap = new Map(columnsArray);

const StopReportPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { classes } = useReportStyles();
  const t = useTranslation();
  const theme = useTheme();

  const devices = useSelector((state) => state.devices.items);
  const printing = useSelector((state) => state.session.printing ?? false);

  const distanceUnit = useAttributePreference('distanceUnit');
  const volumeUnit = useAttributePreference('volumeUnit');

  const [columns, setColumns] = usePersistedState(
    'stopColumns',
    ['startTime', 'endTime', 'startOdometer', 'address'],
  );
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [showCard, setShowCard] = useState(false);
  const [hasResults, setHasResults] = useState(false);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [selectedDeviceIds, setSelectedDeviceIds] = useState([]);
  const [selectedGroupIds, setSelectedGroupIds] = useState([]);

  useEffect(() => {
    const handleAfterPrint = () => dispatch(sessionActions.setPrinting(false));
    window.addEventListener('afterprint', handleAfterPrint);
    return () => {
      window.removeEventListener('afterprint', handleAfterPrint);
    };
  }, [dispatch]);

  useEffect(() => {
    if (printing) {
      requestAnimationFrame(() =>
        requestAnimationFrame(() => nativePostMessage(`print|${t('reportStops')}.pdf`)),
      );
    }
  }, [printing, t]);

  const loadStops = async ({ deviceIds = [], groupIds = [], from, to }) => {
    const query = new URLSearchParams({ from, to });
    deviceIds.forEach((deviceId) => query.append('deviceId', deviceId));
    groupIds.forEach((groupId) => query.append('groupId', groupId));
    setLoading(true);
    try {
      const response = await fetchOrThrow(`/api/reports/stops?${query.toString()}`, {
        headers: { Accept: 'application/json' },
      });
      const data = await response.json();
      setItems(data);
      setHasResults(data.length > 0);
    } finally {
      setLoading(false);
    }
  };

  const onShow = useCatch(async ({ deviceIds, groupIds, from, to }) => {
    setSelectedDeviceIds(deviceIds);
    setSelectedGroupIds(groupIds);
    setFromDate(from || '');
    setToDate(to || '');
    setSelectedItem(null);
    setShowCard(false);
    await loadStops({ deviceIds, groupIds, from, to });
  });

  const getExportValue = (item, key) => {
    const value = item[key];
    switch (key) {
      case 'deviceId':
        return devices[value]?.name || value;
      case 'startTime':
      case 'endTime':
        return formatTime(value, 'minutes');
      case 'startOdometer':
        return formatDistance(value, distanceUnit, t);
      case 'duration':
        return formatNumericHours(value, t);
      case 'engineHours':
        return value > 0 ? formatNumericHours(value, t) : '';
      case 'spentFuel':
        return value > 0 ? formatVolume(value, volumeUnit, t) : '';
      case 'address':
        return item.address || '';
      default:
        return value ?? '';
    }
  };

  const onExport = useCatch(async () => {
    if (!items.length) {
      return;
    }

    const sheets = new Map();
    items.forEach((item) => {
      const deviceName = devices[item.deviceId]?.name || item.deviceId;
      if (!sheets.has(deviceName)) {
        sheets.set(deviceName, []);
      }

      const row = {};
      columns.forEach((key) => {
        const header = t(columnsMap.get(key));
        row[header] = getExportValue(item, key);
      });

      sheets.get(deviceName).push(row);
    });

    await exportExcel(t('reportStops'), `${t('reportStops')}.xlsx`, sheets, theme);
  });

  const onSchedule = useCatch(async (deviceIds, groupIds, report) => {
    report.type = 'stops';
    await scheduleReport(deviceIds, groupIds, report);
    navigate('/reports/scheduled');
  });

  const onPrint = useCatch(async ({ deviceIds = [], groupIds = [], from, to } = {}) => {
    const effectiveDeviceIds = deviceIds.length ? deviceIds : selectedDeviceIds;
    const effectiveGroupIds = groupIds.length ? groupIds : selectedGroupIds;
    const effectiveFrom = from ?? fromDate;
    const effectiveTo = to ?? toDate;

    if (!effectiveDeviceIds.length || !effectiveFrom || !effectiveTo) {
      return;
    }

    if (!items.length) {
      await loadStops({
        deviceIds: effectiveDeviceIds,
        groupIds: effectiveGroupIds,
        from: effectiveFrom,
        to: effectiveTo,
      });
    }

    setSelectedDeviceIds(effectiveDeviceIds);
    setSelectedGroupIds(effectiveGroupIds);
    setFromDate(effectiveFrom || '');
    setToDate(effectiveTo || '');
    dispatch(sessionActions.setPrinting(true));
  });

  const formatValue = (item, key) => {
    const value = item[key];
    switch (key) {
      case 'deviceId':
        return devices[value]?.name || value;
      case 'startTime':
      case 'endTime':
        return formatTime(value, 'minutes');
      case 'startOdometer':
        return formatDistance(value, distanceUnit, t);
      case 'duration':
        return formatNumericHours(value, t);
      case 'engineHours':
        return value > 0 ? formatNumericHours(value, t) : null;
      case 'spentFuel':
        return value > 0 ? formatVolume(value, volumeUnit, t) : null;
      case 'address':
        return (
          <AddressValue
            latitude={item.latitude}
            longitude={item.longitude}
            originalAddress={value}
          />
        );
      default:
        return value;
    }
  };

  const getPrintableValue = (item, key) => {
    const value = item[key];
    switch (key) {
      case 'deviceId':
        return devices[value]?.name || `${value}`;
      case 'startTime':
      case 'endTime':
        return formatTime(value, 'minutes');
      case 'startOdometer':
        return formatDistance(value, distanceUnit, t) || '';
      case 'duration':
        return formatNumericHours(value, t) || '';
      case 'engineHours':
        return value > 0 ? formatNumericHours(value, t) : '';
      case 'spentFuel':
        return value > 0 ? formatVolume(value, volumeUnit, t) : '';
      case 'address':
        return item.address || '';
      default:
        return value != null ? `${value}` : '';
    }
  };

  const onMarkerClick = useCallback(() => {
    setShowCard(true);
  }, []);

  const deviceNames = selectedDeviceIds
    .map((deviceId) => devices[deviceId]?.name || devices[deviceId]?.uniqueId || `${deviceId}`)
    .filter(Boolean)
    .join(', ');

  if (printing) {
    return (
      <div>
        <PrintHeader
          title={t('reportStops')}
          from={fromDate}
          to={toDate}
          device={deviceNames}
        />
        <div>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('sharedDevice')}</TableCell>
                {columns.map((key) => (
                  <TableCell key={key}>{t(columnsMap.get(key))}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((item) => (
                <TableRow key={`print-${item.id}`}>
                  <TableCell>{devices[item.deviceId]?.name || item.deviceId}</TableCell>
                  {columns.map((key) => (
                    <TableCell key={key}>{getPrintableValue(item, key)}</TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    );
  }

  return (
    <PageLayout menu={<ReportsMenu />} breadcrumbs={['reportTitle', 'reportStops']}>
      <div className={classes.container}>
        {selectedItem && (
          <div className={classes.containerMap}>
            <MapView>
              <MapGeofence />
              <MapPositions
                positions={[{
                  id: selectedItem.positionId || selectedItem.id,
                  deviceId: selectedItem.deviceId,
                  fixTime: selectedItem.startTime,
                  latitude: selectedItem.latitude,
                  longitude: selectedItem.longitude,
                  course: selectedItem.course || 0,
                  attributes: selectedItem.attributes || {},
                }]}
                titleField="fixTime"
                onMarkerClick={onMarkerClick}
              />
              {showCard && (
                <StatusCard
                  deviceId={selectedItem.deviceId}
                  position={{
                    id: selectedItem.positionId || selectedItem.id,
                    deviceId: selectedItem.deviceId,
                    fixTime: selectedItem.startTime,
                    address: selectedItem.address,
                    latitude: selectedItem.latitude,
                    longitude: selectedItem.longitude,
                    course: selectedItem.course || 0,
                    attributes: selectedItem.attributes || {},
                    speed: selectedItem.speed,
                    totalDistance: selectedItem.totalDistance || selectedItem.distance,
                  }}
                  onClose={() => setShowCard(false)}
                  disableActions
                />
              )}
            </MapView>
            <MapScale />
            <MapCamera latitude={selectedItem.latitude} longitude={selectedItem.longitude} />
          </div>
        )}
        <div className={classes.containerMain}>
          <div className={classes.header}>
            <ReportFilter
              onShow={onShow}
              onExport={onExport}
              onPrint={onPrint}
              onSchedule={onSchedule}
              deviceType="multiple"
              loading={loading}
              hasResults={hasResults}
            >
              <ColumnSelect columns={columns} setColumns={setColumns} columnsArray={columnsArray} />
            </ReportFilter>
          </div>
          <Table id="stop-report-table">
            <TableHead>
              <TableRow>
                <TableCell className={classes.columnAction} />
                <TableCell>{t('sharedDevice')}</TableCell>
                {columns.map((key) => (
                  <TableCell key={key}>{t(columnsMap.get(key))}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {!loading ? (
                items.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell className={classes.columnAction} padding="none">
                      {selectedItem === item ? (
                        <IconButton size="small" onClick={() => { setSelectedItem(null); setShowCard(false); }}>
                          <GpsFixedIcon fontSize="small" />
                        </IconButton>
                      ) : (
                        <IconButton size="small" onClick={() => { setSelectedItem(item); setShowCard(false); }}>
                          <LocationSearchingIcon fontSize="small" />
                        </IconButton>
                      )}
                    </TableCell>
                    <TableCell>{devices[item.deviceId]?.name || item.deviceId}</TableCell>
                    {columns.map((key) => (
                      <TableCell key={key}>{formatValue(item, key)}</TableCell>
                    ))}
                  </TableRow>
                ))
              ) : (
                <TableShimmer columns={columns.length + 1} startAction />
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </PageLayout>
  );
};

export default StopReportPage;
